import React, { useRef, useEffect, useMemo } from 'react'
import { getClueNumber, getWordAtCell } from '../../utils/gridUtils'

const styles = {
  wrapper: {
    position: 'relative',
    height: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  pauseOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--color-muted)',
  },
  columnsWrapper: {
    display: 'flex',
    gap: '24px',
    flex: 1,
    minHeight: 0,
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  sectionTitle: {
    fontWeight: '700',
    marginBottom: '12px',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    color: '#333',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px',
  },
  cluesList: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: '8px',
  },
  clueItem: {
    padding: '6px 8px 6px 12px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    lineHeight: '1.4',
    borderLeft: '3px solid #fff',
    marginBottom: '2px',
    borderRadius: '2px',
  },
  clueItemActive: {
    background: '#e6f3ff',
    borderLeft: '3px solid #0066cc',
  },
  clueItemSecondary: {
    background: '#f5f5f5',
    borderLeft: '3px solid #ccc',
  },
  clueItemCompleted: {
    color: '#888',
  },
  clueCheckmark: {
    color: '#22a522',
    marginLeft: '6px',
    fontWeight: 'bold',
  },
  clueItemNum: {
    fontWeight: '700',
    marginRight: '8px',
    color: '#333',
    display: 'inline-block',
    minWidth: '24px',
  },
  clueText: {
    color: '#444',
  },
}

export default function CluePanel({
  grid,
  userGrid,
  numberedCells,
  clues,
  selectedCell,
  direction,
  onClueClick,
  isPaused = false,
}) {
  const acrossListRef = useRef(null)
  const downListRef = useRef(null)

  // Get current active clue and the intersecting clue in the other direction
  let activeClueNum = null
  let secondaryClueNum = null
  const otherDirection = direction === 'across' ? 'down' : 'across'

  if (selectedCell && grid && !grid[selectedCell.row][selectedCell.col].isBlack) {
    activeClueNum = getClueNumber(numberedCells, grid, selectedCell.row, selectedCell.col, direction)
    secondaryClueNum = getClueNumber(numberedCells, grid, selectedCell.row, selectedCell.col, otherDirection)
  }

  // Calculate which clues are fully filled
  const completedClues = useMemo(() => {
    if (!userGrid || !grid) return { across: new Set(), down: new Set() }

    const across = new Set()
    const down = new Set()

    // Check across entries
    numberedCells.acrossEntries.forEach(entry => {
      const wordInfo = getWordAtCell(grid, entry.row, entry.col, 'across')
      if (wordInfo && wordInfo.cells) {
        const allFilled = wordInfo.cells.every(
          cell => userGrid[cell.row][cell.col].letter
        )
        if (allFilled) across.add(entry.num)
      }
    })

    // Check down entries
    numberedCells.downEntries.forEach(entry => {
      const wordInfo = getWordAtCell(grid, entry.row, entry.col, 'down')
      if (wordInfo && wordInfo.cells) {
        const allFilled = wordInfo.cells.every(
          cell => userGrid[cell.row][cell.col].letter
        )
        if (allFilled) down.add(entry.num)
      }
    })

    return { across, down }
  }, [userGrid, grid, numberedCells])

  // Auto-scroll to active clue and secondary clue
  useEffect(() => {
    // Scroll to primary (active) clue
    if (activeClueNum) {
      const listRef = direction === 'across' ? acrossListRef : downListRef
      if (listRef.current) {
        const activeElement = listRef.current.querySelector(`[data-clue-num="${activeClueNum}"]`)
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    }

    // Scroll to secondary (intersecting) clue
    if (secondaryClueNum) {
      const secondaryListRef = direction === 'across' ? downListRef : acrossListRef
      if (secondaryListRef.current) {
        const secondaryElement = secondaryListRef.current.querySelector(`[data-clue-num="${secondaryClueNum}"]`)
        if (secondaryElement) {
          secondaryElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    }
  }, [activeClueNum, secondaryClueNum, direction])

  const handleClueClick = (num, dir) => {
    const entries = dir === 'across' ? numberedCells.acrossEntries : numberedCells.downEntries
    const entry = entries.find(e => e.num === num)
    if (entry) {
      onClueClick(entry.row, entry.col, dir)
    }
  }

  return (
    <div style={styles.wrapper}>
      {isPaused && (
        <div style={styles.pauseOverlay}>
          Paused
        </div>
      )}
      <div style={{ ...styles.container, filter: isPaused ? 'blur(8px)' : 'none' }}>
        <div style={styles.columnsWrapper}>
          {/* Across clues column */}
          <div style={styles.column}>
            <div style={styles.sectionTitle}>Across</div>
            <div style={styles.cluesList} ref={acrossListRef}>
              {numberedCells.acrossEntries.map(entry => {
                const isActive = direction === 'across' && activeClueNum === entry.num
                const isSecondary = direction === 'down' && secondaryClueNum === entry.num
                const isCompleted = completedClues.across.has(entry.num)
                return (
                  <div
                    key={entry.num}
                    data-clue-num={entry.num}
                    style={{
                      ...styles.clueItem,
                      ...(isActive ? styles.clueItemActive : {}),
                      ...(isSecondary ? styles.clueItemSecondary : {}),
                      ...(isCompleted && !isActive ? styles.clueItemCompleted : {}),
                    }}
                    onClick={() => handleClueClick(entry.num, 'across')}
                  >
                    <span style={styles.clueItemNum}>{entry.num}</span>
                    <span style={styles.clueText}>
                      {clues.across?.[entry.num] || <em style={{ color: 'var(--color-muted)' }}>—</em>}
                    </span>
                    {isCompleted && <span style={styles.clueCheckmark}>✓</span>}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Down clues column */}
          <div style={styles.column}>
            <div style={styles.sectionTitle}>Down</div>
            <div style={styles.cluesList} ref={downListRef}>
              {numberedCells.downEntries.map(entry => {
                const isActive = direction === 'down' && activeClueNum === entry.num
                const isSecondary = direction === 'across' && secondaryClueNum === entry.num
                const isCompleted = completedClues.down.has(entry.num)
                return (
                  <div
                    key={entry.num}
                    data-clue-num={entry.num}
                    style={{
                      ...styles.clueItem,
                      ...(isActive ? styles.clueItemActive : {}),
                      ...(isSecondary ? styles.clueItemSecondary : {}),
                      ...(isCompleted && !isActive ? styles.clueItemCompleted : {}),
                    }}
                    onClick={() => handleClueClick(entry.num, 'down')}
                  >
                    <span style={styles.clueItemNum}>{entry.num}</span>
                    <span style={styles.clueText}>
                      {clues.down?.[entry.num] || <em style={{ color: 'var(--color-muted)' }}>—</em>}
                    </span>
                    {isCompleted && <span style={styles.clueCheckmark}>✓</span>}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
